import type { ProjectPhase } from './types';

/** Minimale, ontkoppelde input voor de fase-afleiding (geen Supabase-afhankelijkheid). */
export interface PhaseInput {
  /** Mens-event-markers van het project. */
  offerteSentAt: string | null;
  akkoordAt: string | null;
  afgerondAt: string | null;
  /** Aantal configuraties met variant_status 'definitief'. */
  definitiefCount: number;
  /** Statussen van de inkooporders van het project. */
  purchaseOrderStatuses: Array<'draft' | 'ordered' | 'confirmed' | 'delivered'>;
}

/**
 * Leidt de macro-projectfase af. Akkoord/inkoop/levering zijn parallelle sporen (PRD A3);
 * deze functie kiest de verst-bereikte mijlpaal voor lijst-bucketing en navigatie.
 * De fase wordt nergens opgeslagen.
 */
export function derivePhase(input: PhaseInput): ProjectPhase {
  if (input.afgerondAt) return 'afgerond';

  const pos = input.purchaseOrderStatuses;
  const hasPurchasing = pos.length > 0;
  const allDelivered = hasPurchasing && pos.every((s) => s === 'delivered');

  if (allDelivered) return 'geleverd';
  if (hasPurchasing) return 'in_inkoop';
  if (input.akkoordAt) return 'akkoord';
  if (input.definitiefCount > 0) return 'order';
  if (input.offerteSentAt) return 'offerte';
  return 'concept';
}

/** De drie onafhankelijke sporen, voor de detail-UI (badges/acties per track). */
export interface PhaseTracks {
  akkoord: 'open' | 'done';
  inkoop: 'none' | 'partial' | 'ordered';
  levering: 'none' | 'partial' | 'delivered';
}

export function deriveTracks(input: PhaseInput): PhaseTracks {
  const pos = input.purchaseOrderStatuses;
  const ordered = pos.filter((s) => s !== 'draft').length;
  const delivered = pos.filter((s) => s === 'delivered').length;

  return {
    akkoord: input.akkoordAt ? 'done' : 'open',
    inkoop: ordered === 0 ? 'none' : ordered === pos.length ? 'ordered' : 'partial',
    levering: delivered === 0 ? 'none' : delivered === pos.length ? 'delivered' : 'partial',
  };
}

/** Bucket voor de menu-splitsing: orders-weergave vs projecten-weergave. */
export function phaseBucket(phase: ProjectPhase): 'projecten' | 'orders' {
  return phase === 'concept' || phase === 'offerte' ? 'projecten' : 'orders';
}

/** Menselijke statuszin voor de lijstrij (toont de eerstvolgende openstaande actie). */
export function statusSentence(input: PhaseInput): string {
  const phase = derivePhase(input);
  switch (phase) {
    case 'concept':
      return 'Nog aan het bouwen';
    case 'offerte':
      return 'Offerte verstuurd, wacht op akkoord';
    case 'order':
      return input.akkoordAt ? 'Akkoord, nog niks besteld' : 'Order, nog geen akkoord of inkoop';
    case 'akkoord':
      return 'Akkoord, nog niks besteld';
    case 'in_inkoop': {
      const t = deriveTracks(input);
      return t.levering === 'none' ? 'Inkoop loopt' : 'Levering loopt';
    }
    case 'geleverd':
      return 'Alles binnen, klaar om af te ronden';
    case 'afgerond':
      return 'Afgerond';
  }
}
